// M16 KPI — sayaç ve geometri TEK kaynağı (Eng 5A). Grid sayacı, dashboard şerit/sekmeleri,
// müşteri özeti (`clients-summary`) ve DOCX geometri seçimi (K27) buradan beslenir; FE/BE
// kendi kopyasını yazmaz.
import type { KpiReportTableLayout, KpiResolvedGrid } from "../schemas/kpi.js";
import { rowKindsOf } from "./kpi-breakdown.js";

export interface SetFillStatus {
  /** Payda — bkz. `countSetFillStatus`. */
  total: number;
  filled: number;
  missing: number;
  checkPending: number;
  awaitingVerification: number;
  calcError: number;
  notMeasured: number;
}

export interface GridGeometry {
  entityTable: boolean;
  consolidatedYears: boolean;
}

/**
 * Doluluk sayacı — Davranış kuralı 2 ("sayaç paydası tek kural") + K11.
 *
 * - **Kategori düğümü** ve **kırılım grubu** (shared `rowKindsOf`) hücreleri olsa bile HİÇBİR
 *   sayaca girmez; kırılımlı toplam (`total`) gösterge gibi sayılır.
 * - **Payda (`total`)** = türetilmemiş (`isDerived=false`) YAPRAK göstergeler × `grid.years`
 *   içindeki yıllar × `cells`'te hücresi olan işletmeler — hücre başına 1 (hücresi olmayan
 *   işletme/yıl sayılmaz).
 * - `filled` / `notMeasured`: paydadaki hücrenin durumu. `notMeasured` AYRI sayılır, eksik
 *   değildir.
 * - `missing` = paydadaki `empty` + `pending_xxx` hücreler.
 * - **Türetilmiş** yaprak hücreler paydaya GİRMEZ; `calc_error` (HESAPLANAMADI) ve
 *   `hasPending=true` (girdilerinden biri XXX) hücreleri "Eksik"e değil
 *   `checkPending`'e sayılır; `calcError` bunların yalnız `calc_error` olanlarını sayar.
 * - `awaitingVerification` (K11) = yaprak hücrelerde `workflowState === "submitted"` ya da
 *   `client_approved` (TODO-KPI-10: şirket onayı danışman onayının yerine geçmez) — türetilmiş
 *   dahil; C1 yayılımıyla `unverified` değil, yalnız gerçek durum.
 *
 * `filled + missing + notMeasured === total` her zaman sağlanır.
 */
export function countSetFillStatus(grid: KpiResolvedGrid): SetFillStatus {
  const status: SetFillStatus = {
    total: 0,
    filled: 0,
    missing: 0,
    checkPending: 0,
    awaitingVerification: 0,
    calcError: 0,
    notMeasured: 0,
  };
  // TODO-KPI-110: satır türü TEK kaynaktan — kırılımlı toplam (`total`) değer taşır ve sayılır,
  // kategori ve kırılım grubu satırı sayılmaz (kırılımsız sette eski "çocuklu = kategori" ile aynı).
  const kinds = rowKindsOf(grid.indicators);
  for (const ind of grid.indicators) {
    const kind = kinds.get(ind.id);
    if (kind === "category" || kind === "breakdown_group") continue;
    for (const byYear of Object.values(ind.cells)) {
      for (const year of grid.years) {
        const cell = byYear[year];
        if (!cell) continue;
        if (cell.workflowState === "submitted" || cell.workflowState === "client_approved")
          status.awaitingVerification += 1;
        if (ind.isDerived) {
          if (cell.status === "calc_error") {
            status.calcError += 1;
            status.checkPending += 1;
          } else if (cell.hasPending) {
            status.checkPending += 1;
          }
          continue;
        }
        status.total += 1;
        switch (cell.status) {
          case "filled":
            status.filled += 1;
            break;
          case "not_measured":
            status.notMeasured += 1;
            break;
          case "calc_error":
            // Türetilmemiş hücrede beklenmez; yine de paydada tutup eksik sayarız.
            status.missing += 1;
            break;
          default:
            status.missing += 1;
        }
      }
    }
  }
  return status;
}

/**
 * K27 — grid/DOCX geometrisi. Aktif işletme ≤1 ise düzen ne olursa olsun yalnız konsolide
 * yıllar tablosu (işletme tablosu anlamsız); >1 ise set ayarı (`reportTableLayout`) belirler.
 */
export function selectGridGeometry(
  entities: readonly { id: string; isActive: boolean }[],
  layout: KpiReportTableLayout,
): GridGeometry {
  const active = entities.filter((e) => e.isActive).length;
  if (active <= 1) return { entityTable: false, consolidatedYears: true };
  switch (layout) {
    case "entities":
      return { entityTable: true, consolidatedYears: false };
    case "consolidated":
      return { entityTable: false, consolidatedYears: true };
    case "both":
      return { entityTable: true, consolidatedYears: true };
  }
}

/**
 * Yüzde değişim: (current − previous) / |previous| × 100. Önceki 0 ya da sonlu olmayan
 * girdi → null (Değişim kolonu "—"). Mutlak payda: negatif tabanda yön korunur.
 */
export function changePercent(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/**
 * Değişim yönü semantiği (plan "Değişim yönü semantiği"): `increaseIsGood` true → ↑iyi/↓kötü;
 * false (kaza, emisyon) → ↓iyi/↑kötü; null → nötr. pct null ya da 0 → nötr.
 */
export function changeTone(
  pct: number | null,
  increaseIsGood: boolean | null,
): "good" | "bad" | "neutral" {
  if (pct === null || pct === 0 || increaseIsGood === null || !Number.isFinite(pct)) {
    return "neutral";
  }
  const up = pct > 0;
  return up === increaseIsGood ? "good" : "bad";
}
